import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Button, Page, TextButton } from '@zillow/constellation-10';

import {
  EnterprisePageFeature,
  RentalManagerEnterpriseEventTrackingWebApiHandler_trackPageVisit,
} from '@zg-rentals/ts-rental-manager-api-beta-client';
import {
  useRentalManagerUserClaimableListingsWebApiHandler_getClaimableProperties,
  useRentalManagerUserClaimableListingsWebApiHandler_userHasNewPropertiesToClaim,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import accessUtils from '../../../srcLegacy/shared/utils/accessUtils';
import { useGetPropertiesData, useSpotlightOverview } from '../../hooks/queries/properties/zram';
import { useScreenSize } from '../../hooks/useScreenSize';
import {
  closeBanner,
  initializeFiltersFromRouter,
  isFromMarketingApplicationsRoute,
  isFromMarketingLeasesRoute,
  isFromMarketingPaymentsRoute,
  isRedirectToPayments,
  removeQueryParams,
  renderInitialDisplayModal,
  updateRouterWithFilters,
} from '../../utils/propertiesPageUtils';

import {
  CLAIM_YOUR_LISTING_COOKIES,
  SPOTLIGHT_LISTINGS_COOKIES,
} from '../../../srcLegacy/shared/constants/CookieConstants';
import QueryConstants from '../../../srcLegacy/shared/constants/QueryConstants';
import PropertiesConstants from '../../constants/PropertiesConstants';
import { EXTERNAL_ROUTES, ROUTES } from '../../constants/urls';

import {
  emitAddFeedClickEvent,
  emitAddPropertyClickEvent,
  emitOpenEvent,
} from '../../analytics/events/addPropertyModal';
import { useAuth } from '../../components/AuthProvider';
import AddFeedListingsModal from '../../components/modals/AddFeedListingsModal';
import AddPropertyModal from '../../components/modals/AddPropertyModal';
import ClaimPropertiesModal from '../../components/modals/ClaimPropertiesModal';
import SpotlightListingsFirstUseModal from '../../components/modals/SpotlightListingsFirstUseModal';
import PostClaimingModal from '../../components/PostClaimingModal';
import EmailVerification from '../../components/properties/EmailVerification';
import NotificationBanner from '../../components/properties/NotificationBanner';
import Notifications from '../../components/properties/Notifications';

import {
  emitViewPropertiesPageEvent,
  EVENT_IDS as PROPERTIES_PAGE_EVENT_IDS,
} from '../../analytics/events/propertiesPage';
import AuthCheckWrapper from '../AuthCheckWrapper';
import ClaimPathBanners from './ClaimPathBanners';
import { Filters } from './Filters';
import { PageHeading } from './PageHeading';
import { PropertyTable } from './PropertyTable';
import SpotlightListingsBanner from './SpolightListingsBanner';

import type { SetStateAction } from 'react';
import type { ZrmClickstreamUser } from '@zg-rentals/clickstream';
import type { AccountViewListingsDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { FilterState } from '../../utils/propertiesPageUtils';
import type { BannerToCloseArg } from '../PropertiesPage';

const defaultFilters = {
  searchText: PropertiesConstants.PROPERTIES_SEARCHTEXT_DEFAULT,
  subNavFilterType: PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ALL,
};

export const ZRAMPropertiesPage = () => {
  const router = useRouter();
  const { user } = useAuth();
  const { isScreenMobile } = useScreenSize();

  const isCheckInitialDisplayModal = useRef(true);

  const [filters, setFilters] = useState<FilterState>(() => initializeFiltersFromRouter(router.query, defaultFilters));
  const [isValidatingFilters, setIsValidatingFilters] = useState(true);
  const [showAddFeedListingsModal, setShowAddFeedListingsModal] = useState(false);
  const [showClaimPropertiesModal, setShowClaimPropertiesModal] = useState({
    isOpen: false,
    isClaimMoreModal: false,
  });
  const [showPostClaimingModal, setShowPostClaimingModal] = useState(false);
  const [showSpotlightListingsFirstUseModal, setShowSpotlightListingsFirstUseModal] = useState(false);
  const [showAddPropertyModal, setShowAddPropertyModal] = useState(false);

  const [showPaymentsNotification, setDisplayPaymentsNotification] = useState(false);
  const [showApplicationsNotification, setDisplayApplicationsNotification] = useState(false);
  const [showLeasesNotification, setDisplayLeasesNotification] = useState(false);
  const [showListingClaimCompleteBanner, setDisplayListingClaimCompleteBanner] = useState(false);
  const isNonAdvertiser = user ? accessUtils.isNonAdvertiser({ user }) : false;

  const {
    data: propertiesData,
    isPending: isPropertiesPending,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetPropertiesData(filters);

  const { data: spotlightOverviewData, isPending: isSpotlightOverviewPending } = useSpotlightOverview();

  const {
    total: numTotalListings = 0,
    freeClaimedListingCount: numClaimedListings = 0,
    archived: numArchivedListings = 0,
    freeBonusListingCount: numBonusListings = 0,
    atRisk: numAtRiskListings = 0,
  } = propertiesData?.listingCount || {};

  const { data: { newPropertiesCount = 0, hasNewProperties: hasNewPropertiesToClaim = false } = {} } =
    useRentalManagerUserClaimableListingsWebApiHandler_userHasNewPropertiesToClaim({
      enabled: user?.features?.canClaimFreeListings ?? false,
    });
  const { data: { unitProperties: claimableProperties = [] } = {}, isFetching: isClaimablePropertiesFetching } =
    useRentalManagerUserClaimableListingsWebApiHandler_getClaimableProperties(
      {},
      {
        enabled: user?.features?.canClaimFreeListings ?? false,
      },
    );

  const canAccessClaimPathModal = accessUtils.canAccessClaimPath({
    isScreenMobile,
    hasAnyPropertiesToClaim: claimableProperties.length > 0,
    cookie: CLAIM_YOUR_LISTING_COOKIES.HAS_CLOSED_MODAL,
    isNonAdvertiser,
  });

  const canAccessClaimPathMorePropertiesModal = accessUtils.canAccessClaimMorePath({
    isScreenMobile,
    hasAnyClaimedListings: numClaimedListings > 0,
    hasNewPropertiesToClaim,
    cookie: CLAIM_YOUR_LISTING_COOKIES.HAS_CLOSED_CLAIM_MORE_MODAL,
  });

  const canAccessSpotlightListingsFirstUseModal = accessUtils.canAccessSpotlightListingsFirstUseModal({
    isScreenMobile,
    isSpotlightListingsEligible: Boolean(spotlightOverviewData?.isEligible),
    cookie: SPOTLIGHT_LISTINGS_COOKIES.HAS_CLOSED_SPOTLIGHT_LISTINGS_MODAL,
  });

  const queryParams = router.query;

  const isArchivedView = filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED;

  useEffect(() => {
    // Scroll to top of page on initial load and when switching between views
    window.scrollTo(0, 0);

    // Clear search text when switching to archived view
    if (isArchivedView) {
      setFilters({ ...filters, searchText: '' });
    }
  }, [isArchivedView]);

  useEffect(() => {
    const query = router.query;
    const isFromMarketingPayments = isFromMarketingPaymentsRoute(query);
    const isFromMarketingLeases = isFromMarketingLeasesRoute(query);
    const isFromMarketingApplications = isFromMarketingApplicationsRoute(query);

    if (user?.restrictions?.isAllowed) {
      if (isFromMarketingPayments) {
        setDisplayPaymentsNotification(true);
      } else if (isFromMarketingLeases) {
        setDisplayLeasesNotification(true);
      } else if (isFromMarketingApplications && user.applicationsEnabled) {
        setDisplayApplicationsNotification(true);
      }
    }

    emitViewPropertiesPageEvent({ user });
  }, []);

  useEffect(() => {
    // Once data has loaded, validate if user can access the requested tab
    if (!isPropertiesPending && !isClaimablePropertiesFetching) {
      const bypassOlympusRedirect = queryParams.bypassOlympusRedirect === 'true';
      if (isNonAdvertiser && numTotalListings === 0 && !bypassOlympusRedirect) {
        window.location.href = EXTERNAL_ROUTES.OLYMPUS_REDIRECT;
      }

      const validatedFilters = validateFiltersAccess(initializeFiltersFromRouter(router.query, filters));
      setFilters(validatedFilters);

      if (validatedFilters.subNavFilterType !== filters.subNavFilterType) {
        updateRouterWithFilters(router, validatedFilters);
      }

      setIsValidatingFilters(false);
    }
  }, [router.asPath, isPropertiesPending, isClaimablePropertiesFetching]);

  useEffect(() => {
    if (!queryParams.subNavFilterType) {
      return;
    }
    if (user?.customers?.length && !user.impersonatorEmail && !user.impersonatorUserToken) {
      let pageViewType;
      switch (filters.subNavFilterType) {
        case PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ALL:
          pageViewType = EnterprisePageFeature.MY_PROPERTIES;
          break;
        case PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ON_MARKET:
          pageViewType = EnterprisePageFeature.MY_PROPERTIES_FOR_RENT;
          break;
        case PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.OFF_MARKET:
          pageViewType = EnterprisePageFeature.MY_PROPERTIES_OFF_MARKET;
          break;
        case PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.AT_RISK:
          pageViewType = EnterprisePageFeature.MY_PROPERTIES_AT_RISK;
          break;
        case PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.FREE_BONUS_LISTINGS:
          pageViewType = EnterprisePageFeature.MY_PROPERTIES_BONUS;
          break;
        case PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.CLAIMED:
          pageViewType = EnterprisePageFeature.MY_PROPERTIES_CLAIMED;
          break;
      }
      if (pageViewType) {
        RentalManagerEnterpriseEventTrackingWebApiHandler_trackPageVisit({ page: pageViewType }).catch(() => {});
      }
    }
  }, [filters.subNavFilterType, router.asPath]);

  // Check for initial modals once all data has loaded
  useEffect(() => {
    if (isCheckInitialDisplayModal.current && !isPropertiesPending && !isSpotlightOverviewPending) {
      isCheckInitialDisplayModal.current = false;
      checkInitialDisplayModal();
    }
  }, [isPropertiesPending, isSpotlightOverviewPending]);

  // Re-check for modals when specific query params change (e.g., from left nav)
  useEffect(() => {
    const postingPath = router.query[QueryConstants.POSTING_PATH];
    const addAFeed = router.query[QueryConstants.ADD_A_FEED];

    if (postingPath || addAFeed) {
      checkInitialDisplayModal();
    }
  }, [router.query[QueryConstants.POSTING_PATH], router.query[QueryConstants.ADD_A_FEED]]);

  const updateFilters = (newFilters: SetStateAction<FilterState>) => {
    setFilters((current) => {
      const next = typeof newFilters === 'function' ? newFilters(current) : newFilters;
      updateRouterWithFilters(router, next);
      return next;
    });
  };

  const validateFiltersAccess = (filters: FilterState): FilterState => {
    const { subNavFilterType } = filters;
    const canAccessClaimed =
      numClaimedListings > 0 ||
      accessUtils.canAccessClaimPath({
        isScreenMobile,
        hasAnyPropertiesToClaim: claimableProperties.length > 0,
        isNonAdvertiser: user ? accessUtils.isNonAdvertiser({ user }) : false,
      });

    if (
      (subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.CLAIMED && !canAccessClaimed) ||
      (subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.AT_RISK && numAtRiskListings === 0) ||
      (subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.FREE_BONUS_LISTINGS &&
        numBonusListings === 0) ||
      !Object.values(PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE).includes(subNavFilterType)
    ) {
      return {
        ...filters,
        subNavFilterType: PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ALL,
      };
    }
    return filters;
  };

  const checkInitialDisplayModal = () => {
    renderInitialDisplayModal({
      router,
      totalListingsWithoutFilters: numTotalListings,
      showAddPropertyModal,
      setShowAddPropertyModal: handleAddPropertyClick,
      canAccessOnboardingWizard: false,
      properties: propertiesData as AccountViewListingsDto,
      setShowAddFeedListingsModal: () => {
        if (user?.restrictions?.isAllowed && accessUtils.canAccessAddFeedListingsModal({ user })) {
          setShowAddFeedListingsModal(true);
        }
      },
      setShowPostClaimingModal,
      setShowClaimPropertiesModal,
      canAccessClaimPathModal,
      canAccessClaimPathMorePropertiesModal,
      setShowSpotlightListingsFirstUseModal,
      canAccessSpotlightListingsFirstUseModal,
    });
  };

  const handleAddPropertyClick = ({ shouldSendAnalytics = false }: { shouldSendAnalytics?: boolean }) => {
    const shouldRedirectToPayments = isRedirectToPayments(router.query);

    if (user?.features?.canAddListing && user?.restrictions?.isAllowed) {
      setShowAddPropertyModal(true);
    }

    if (shouldSendAnalytics) {
      emitOpenEvent({
        user: user as ZrmClickstreamUser,
        triggerObject: shouldRedirectToPayments ? 'start_collecting_rent_button' : undefined,
      });

      emitAddPropertyClickEvent({ user: user as ZrmClickstreamUser });
    }
  };

  const handleAddFeedListingsClick = () => {
    setShowAddFeedListingsModal(true);
    emitAddFeedClickEvent({ user: user as ZrmClickstreamUser });
  };

  const onBannerClose = (bannerToClose: BannerToCloseArg) => {
    closeBanner(
      bannerToClose,
      setDisplayPaymentsNotification,
      setDisplayApplicationsNotification,
      setDisplayLeasesNotification,
      setDisplayListingClaimCompleteBanner,
    );
  };

  const handleViewArchivedPropertiesClick = () => {
    router.push({
      pathname: ROUTES.PROPERTIES,
      query: {
        [QueryConstants.SUB_NAV_FILTER_TYPE]: PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED,
      },
    });
  };

  return (
    <AuthCheckWrapper
      loading={<PropertyTable filters={filters} setFilters={updateFilters} isSkeletonView={isValidatingFilters} />}
      redirecting={<PropertyTable filters={filters} setFilters={updateFilters} isSkeletonView={isValidatingFilters} />}
    >
      <Page.Root
        data-testid="ZRAMPropertiesPage"
        data-panda-theme="zillow"
        data-eventid={PROPERTIES_PAGE_EVENT_IDS.VIEW_PROPERTIES_PAGE}
        data-eventdescription="View properties page"
      >
        <Page.Header
          css={{
            padding: '0 !important',
            margin: '0 !important',
            maxWidth: 'none',
            borderBottom: '1px solid {colors.border.muted}',
          }}
        >
          <PageHeading
            handleAddPropertyClick={handleAddPropertyClick}
            handleAddFeedListingsClick={handleAddFeedListingsClick}
            isArchivedView={isArchivedView}
          />
        </Page.Header>
        <Page.Content css={{ padding: { base: '0 {spacing.600}', xl: '0 {spacing.1200}' }, maxWidth: '100%' }}>
          {/* Banners */}
          {user?.features?.canAccessFeatureLaunchNotifications && <NotificationBanner />}
          <Notifications
            displayPaymentsNotification={showPaymentsNotification}
            displayApplicationsNotification={showApplicationsNotification}
            displayLeasesNotification={showLeasesNotification}
            displayListingClaimCompleteBanner={showListingClaimCompleteBanner}
            handleBannerCloseClick={onBannerClose}
          />
          <EmailVerification />
          <ClaimPathBanners
            hasAnyClaimedListings={numClaimedListings > 0}
            subNavFilterType={filters.subNavFilterType}
          />
          {spotlightOverviewData?.isEligible && !isArchivedView && !isSpotlightOverviewPending && (
            <SpotlightListingsBanner
              hasSpotlightedListings={spotlightOverviewData?.hasSpotlightedListings ?? false}
              availableSpotlightListings={spotlightOverviewData?.availableCount ?? 0}
            />
          )}
          {/* Table */}
          {isArchivedView ? null : <Filters filters={filters} setFilters={updateFilters} />}
          <PropertyTable filters={filters} setFilters={updateFilters} isSkeletonView={isValidatingFilters} />
          {/* CTAs */}
          {numArchivedListings > 0 && !isArchivedView && (
            <TextButton onClick={handleViewArchivedPropertiesClick} css={{ marginTop: '600' }}>
              View archived properties
            </TextButton>
          )}
          {hasNextPage && (
            <Box css={{ display: 'flex', justifyContent: 'center', marginTop: '600' }}>
              <Button tone="brand" emphasis="filled" onClick={() => fetchNextPage()} loading={isFetchingNextPage}>
                Load more properties
              </Button>
            </Box>
          )}
        </Page.Content>
        {showAddFeedListingsModal && (
          <AddFeedListingsModal
            onClose={() => {
              removeQueryParams(router, [QueryConstants.ADD_A_FEED]);
              setShowAddFeedListingsModal(false);
            }}
          />
        )}
        {showAddPropertyModal && (
          <AddPropertyModal
            isOpen={showAddPropertyModal}
            onClose={() => {
              removeQueryParams(router, [QueryConstants.POSTING_PATH, QueryConstants.REDIRECT_TO]);
              setShowAddPropertyModal(false);
            }}
          />
        )}
        {!isScreenMobile && showClaimPropertiesModal.isOpen && (
          <ClaimPropertiesModal
            onClose={() => {
              setShowClaimPropertiesModal({ ...showClaimPropertiesModal, isOpen: false });
            }}
            isClaimMoreModal={showClaimPropertiesModal.isClaimMoreModal}
            newPropertiesCount={newPropertiesCount}
          />
        )}
        {showPostClaimingModal && (
          <PostClaimingModal
            onClose={() => {
              removeQueryParams(router, [QueryConstants.CLAIM_DASHBOARD]);
              setShowPostClaimingModal(false);
            }}
          />
        )}
        {showSpotlightListingsFirstUseModal && (
          <SpotlightListingsFirstUseModal onClose={() => setShowSpotlightListingsFirstUseModal(false)} />
        )}
      </Page.Root>
    </AuthCheckWrapper>
  );
};
